
import { CreditCard } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

interface BalanceCardProps {
  title: string;
  totalAmount: number;
  currentAmount: number;
  icon?: React.ReactNode;
  variant?: "default" | "income" | "expense";
  showProgress?: boolean;
}

export function BalanceCard({
  title,
  totalAmount,
  currentAmount,
  icon = <CreditCard className="h-5 w-5" />,
  variant = "default",
  showProgress = false,
}: BalanceCardProps) {
  const formattedAmount = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(currentAmount);

  const progressPercentage = totalAmount > 0 
    ? Math.min(Math.round((currentAmount / totalAmount) * 100), 100) 
    : 0;

  const getVariantStyles = () => {
    switch (variant) {
      case "income":
        return "bg-finance-green/10 text-finance-green border-finance-green/20 hover:bg-finance-green/20";
      case "expense":
        return "bg-finance-red/10 text-finance-red border-finance-red/20 hover:bg-finance-red/20";
      default:
        return "bg-primary/10 text-primary border-primary/20 hover:bg-primary/20";
    }
  };

  const getProgressColor = () => {
    switch (variant) {
      case "income":
        return "[&>div]:bg-finance-green";
      case "expense":
        return "[&>div]:bg-finance-red";
      default:
        return "[&>div]:bg-primary";
    }
  };

  const getCardVariant = () => {
    switch (variant) {
      case "income":
        return "border-l-4 border-l-finance-green hover:shadow-finance-green/20";
      case "expense":
        return "border-l-4 border-l-finance-red hover:shadow-finance-red/20";
      default:
        return "border-l-4 border-l-primary hover:shadow-primary/20";
    }
  };

  return (
    <Card className={cn(
      "card-hover overflow-hidden transition-all duration-300 hover:shadow-lg group border-0 dark:bg-muted/50 dark:border-border/50",
      getCardVariant()
    )}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-sm font-semibold text-muted-foreground group-hover:text-foreground transition-colors">
          {title}
        </CardTitle>
        <div className={cn(
          "rounded-xl p-2.5 border transition-all duration-200 group-hover:scale-110",
          getVariantStyles()
        )}>
          {icon}
        </div>
      </CardHeader>
      <CardContent className="pb-4">
        <div className="text-3xl font-bold text-foreground mb-2 group-hover:scale-105 transition-transform duration-200">
          {formattedAmount}
        </div>
        
        {showProgress && (
          <div className="space-y-2">
            <Progress 
              value={progressPercentage} 
              className={cn("h-2 bg-muted", getProgressColor())}
            />
            <div className="flex justify-between text-xs">
              <span className="font-medium text-muted-foreground">
                {progressPercentage}% realizado
              </span>
              <span className="font-medium text-muted-foreground">
                Meta: {new Intl.NumberFormat("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                }).format(totalAmount)}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
